import { type ReactCustomBlockRenderProps, useExtensionState } from '@blocknote/react';
import { useEffect } from 'react';
import { nanoid } from 'shared/utils/nanoid';
import { checkedExtension } from '~/modules/common/blocknote/custom-elements/checklist/checklist-extension';
import type { checklistItemConfig } from '~/modules/common/blocknote/custom-elements/checklist/checklist-item-block';
import { updateBlockWithoutHistory } from '~/modules/common/blocknote/helpers/blocknote-helpers';
import { cn } from '~/utils/cn';

type ChecklistItemRenderProps = ReactCustomBlockRenderProps<typeof checklistItemConfig>;

export function ChecklistItemRender({ block, editor, contentRef }: ChecklistItemRenderProps) {
  // Assign a checkboxId when missing or duplicated, which happens when Enter splits a block.
  useEffect(() => {
    const id = block.props.checkboxId;

    if (!id) {
      // Defer to avoid flushSync inside React lifecycle
      setTimeout(() => updateBlockWithoutHistory(editor, block, { props: { checkboxId: nanoid(12) } }), 0);
      return;
    }

    // Detect duplicate checkboxIds from block splits where both halves get the same props.
    for (const b of editor.document) {
      if (b.type !== 'checklistItem') continue;
      if ((b.props as { checkboxId?: string }).checkboxId !== id) continue;
      if (b.id === block.id) break; // This is the first occurrence, keep its ID
      setTimeout(() => updateBlockWithoutHistory(editor, block, { props: { checkboxId: nanoid(12) } }), 0);
      break;
    }
  }, [block.id, block.props.checkboxId, editor]);

  const { persisted } = useExtensionState(checkedExtension, { editor });
  const isChecked = block.props.checked ?? false;

  const handleToggle = () => {
    if (!persisted) return;
    // In collaborative mode this block update is a Y.Doc update.
    editor.updateBlock(block, { props: { checked: !isChecked } });
  };

  return (
    <div className="checklist-item" data-checked={isChecked}>
      <div contentEditable={false} className="checklist-checkbox-wrapper">
        <input
          type="checkbox"
          checked={isChecked}
          disabled={!persisted}
          onChange={handleToggle}
          data-checkbox-id={block.props.checkboxId}
          className="checklist-checkbox"
        />
      </div>
      <p className={cn('checklist-content', isChecked && 'checklist-checked')} ref={contentRef} />
    </div>
  );
}
